'use client';

import { useEffect, useRef, useState } from 'react';
import PhoneFrame from './PhoneFrame';
import { MATRIX_DIGEST, QR } from './qr-matrix';
import QrSvg from './QrSvg';

/**
 * §2 — the split-screen simultaneity card, the page's ONE signature device.
 * Left pane: the printed flyer. It NEVER moves — no animated geometry, ever;
 * the only thing that changes is the ✓-verified opacity. Right pane: what
 * scanning the same code resolves to, cycling three destinations across a
 * season. The stillness of the left pane against the motion of the right IS
 * the argument.
 *
 * Motion contract (handoff, binding): 2600 ms real-clock beat — deliberately
 * a plain interval + CSS transitions, NOT the site's GSAP clock (rAF starves
 * in occluded tabs; a skipped beat must pause the story, not queue it).
 * Gates re-checked every beat: tab visible, no reduced-motion, md+, card
 * ≥25% in view. The SSR/static fallback IS the finished state (REST below:
 * SEP 21 / event recap / ✓ dim), so no-JS, reduced-motion and mobile all
 * rest on the story's conclusion.
 */

const MOTION = true;

const STEPS = [
  { date: 'MAY 12', dest: 'acme.com/summer-menu' },
  { date: 'JUL 03', dest: 'acme.com/waitlist' },
  { date: 'SEP 21', dest: 'acme.com/event-recap' },
] as const;

type HeroState = {
  phase: 0 | 1 | 2 | 3;
  step: 0 | 1 | 2;
  hit: boolean;
  check: boolean;
};

const REST: HeroState = { phase: 3, step: 2, hit: false, check: false };

/* ------------------------------------------------------------------ */
/* Phone screens — acme fiction, authored in the frame's 402×874 space  */
/* (system fonts + raw hex by design: it's the customer's site, not     */
/* rii.link chrome).                                                    */
/* ------------------------------------------------------------------ */

function Screen({ visible, children }: { visible: boolean; children: React.ReactNode }) {
  return (
    <div
      className="absolute inset-0 flex flex-col gap-[18px] px-[26px] pb-[52px] pt-[76px] transition-opacity duration-[550ms] ease-out"
      style={{ opacity: visible ? 1 : 0 }}
    >
      {children}
    </div>
  );
}

function UrlPill({ children }: { children: React.ReactNode }) {
  return (
    <span className="self-center rounded-full bg-[#E4E4E9] px-5 py-[9px] text-[14px] text-[#5f5f66]">
      {children}
    </span>
  );
}

function MenuScreen({ visible }: { visible: boolean }) {
  const rows = [
    ['Tomato & burrata', '9'],
    ['Grilled peach salad', '11'],
    ['Flat iron, chimichurri', '19'],
    ['Cold brew tonic', '5'],
  ];
  return (
    <Screen visible={visible}>
      <UrlPill>acme.com/summer-menu</UrlPill>
      <span className="mt-2.5 px-1 text-[30px] font-bold tracking-[-0.02em]">Summer menu</span>
      <div className="flex flex-col rounded-[18px] bg-white px-[18px] py-1.5">
        {rows.map(([name, price], i) => (
          <span
            key={name}
            className={`flex justify-between py-[15px] text-[16.5px] ${
              i < rows.length - 1 ? 'border-b border-[rgba(16,16,40,.07)]' : ''
            }`}
          >
            <span>{name}</span>
            <span className="text-[#8a8a93]">{price}</span>
          </span>
        ))}
      </div>
      <span className="mt-auto px-1 text-[14px] text-[#8a8a93]">
        Fridays 17:00 &ndash; 23:00 &middot; Ritterstr. 12
      </span>
    </Screen>
  );
}

function WaitlistScreen({ visible }: { visible: boolean }) {
  return (
    <Screen visible={visible}>
      <UrlPill>acme.com/waitlist</UrlPill>
      <div className="mt-2.5 flex flex-col gap-1.5 rounded-[14px] border border-[color-mix(in_srgb,var(--color-data-amber)_45%,transparent)] bg-[color-mix(in_srgb,var(--color-data-amber)_12%,white)] px-[18px] py-4">
        <span className="text-[12px] font-semibold tracking-[0.12em]">SOLD OUT</span>
        <span className="text-[14.5px] leading-[1.5] text-[#5f5f66]">
          Tonight&rsquo;s patio tasting is full.
        </span>
      </div>
      <div className="flex flex-col gap-3.5 rounded-[18px] bg-white px-[18px] py-5">
        <span className="text-[22px] font-bold tracking-[-0.02em]">Join the waitlist</span>
        <span className="rounded-xl border border-[rgba(16,16,40,.14)] px-[15px] py-[13px] text-[15px] text-[#8a8a93]">
          you@email.com
        </span>
        <span className="rounded-xl bg-[#101028] px-[15px] py-3.5 text-center text-[15.5px] font-semibold text-white">
          Notify me
        </span>
      </div>
      <span className="mt-auto px-1 text-[14px] text-[#8a8a93]">72 people ahead of you</span>
    </Screen>
  );
}

function RecapScreen({ visible }: { visible: boolean }) {
  return (
    <Screen visible={visible}>
      <UrlPill>acme.com/event-recap</UrlPill>
      <span className="mt-2.5 px-1 text-[30px] font-bold tracking-[-0.02em]">That was summer.</span>
      <span className="px-1 text-[15px] leading-[1.55] text-[#5f5f66]">
        The series is over &mdash; photos below, October dates soon.
      </span>
      <div className="grid grid-cols-2 gap-2.5 rounded-[18px] bg-white p-3.5">
        <span className="aspect-[4/3] rounded-[10px] bg-[#E4E4E9]" />
        <span className="aspect-[4/3] rounded-[10px] bg-[#d8d8de]" />
        <span className="aspect-[4/3] rounded-[10px] bg-[#d8d8de]" />
        <span className="aspect-[4/3] rounded-[10px] bg-[#E4E4E9]" />
      </div>
      <span className="mt-auto px-1 text-[15px] font-semibold text-brand-600">
        See all photos &rarr;
      </span>
    </Screen>
  );
}

/* ------------------------------------------------------------------ */

export default function HeroSplitCard() {
  const [s, setS] = useState<HeroState>(REST);
  const cardRef = useRef<HTMLDivElement>(null);
  const visibleRef = useRef(false);

  useEffect(() => {
    if (!MOTION) return;
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mqWide = window.matchMedia('(min-width: 768px)');
    const io = new IntersectionObserver(
      (entries) => {
        visibleRef.current = entries[entries.length - 1].isIntersecting;
      },
      { threshold: 0.25 },
    );
    if (cardRef.current) io.observe(cardRef.current);

    const allowed = () =>
      !document.hidden && !mqReduce.matches && mqWide.matches && visibleRef.current;

    const id = setInterval(() => {
      if (!allowed()) return;
      setS((prev) => {
        const phase = ((prev.phase + 1) % 4) as HeroState['phase'];
        const step = Math.min(phase, 2) as HeroState['step'];
        const changed = step !== prev.step;
        return { phase, step, hit: changed, check: changed };
      });
    }, 2600);

    return () => {
      clearInterval(id);
      io.disconnect();
    };
  }, []);

  // Arrival flash decays after 900 ms — kept out of the state updater so the
  // updater stays pure (StrictMode-safe); re-arms on every false→true edge.
  useEffect(() => {
    if (!s.hit) return;
    const t = setTimeout(() => setS((p) => ({ ...p, hit: false, check: false })), 900);
    return () => clearTimeout(t);
  }, [s.hit]);

  const { step, hit, check } = s;

  return (
    <div
      ref={cardRef}
      className="mt-16 grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-px overflow-hidden rounded-ref-lg border border-line-2 bg-line-2"
    >
      <p className="sr-only">
        Demonstration: the printed flyer&rsquo;s QR code never changes &mdash; matrix{' '}
        {MATRIX_DIGEST}, zero reprints &mdash; while the destination it resolves to changed three
        times over one summer: a menu in May, a waitlist in July, an event recap in September.
      </p>

      {/* LEFT — THE PRINT. This pane never moves. */}
      <div className="flex flex-col gap-5 bg-surface px-7 pb-[22px] pt-6">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="font-jetbrains text-[10.5px] tracking-[0.15em] text-dim">
            THE PRINT
          </span>
          <span className="rounded-full border border-line-3 bg-bg-warm px-[9px] py-0.5 font-jetbrains text-[10px] tracking-[0.08em] text-ink">
            NEVER CHANGES
          </span>
        </div>
        <div className="flex flex-1 items-center justify-center py-3.5">
          <div
            aria-hidden="true"
            className="relative w-[min(340px,100%)] border border-line-2 bg-white px-[30px] pb-[26px] pt-8 shadow-[0_20px_46px_-24px_rgba(16,16,40,.3)]"
          >
            <span className="absolute -left-[9px] -top-[9px] h-[9px] w-[9px] border-b border-r border-line-3" />
            <span className="absolute -right-[9px] -top-[9px] h-[9px] w-[9px] border-b border-l border-line-3" />
            <span className="absolute -bottom-[9px] -left-[9px] h-[9px] w-[9px] border-r border-t border-line-3" />
            <span className="absolute -bottom-[9px] -right-[9px] h-[9px] w-[9px] border-l border-t border-line-3" />
            <span className="block font-onest text-[21px] font-extrabold leading-[1.1] tracking-[-0.02em]">
              SUMMER
              <br />
              AT ACME
            </span>
            <span className="mt-2 block text-[11.5px] text-muted">
              Fridays on the patio &mdash; menu, hours, tickets
            </span>
            <div className="mt-[18px] flex items-end gap-3.5">
              <QrSvg path={QR.sq} width={118} className="block flex-none" />
              <div className="flex flex-col gap-1 pb-1">
                <span className="text-[10.5px] text-muted">scan for this week</span>
                <span className="font-jetbrains text-[10px] text-ink">go.acme.com/menu</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-[5px] border-t border-line pt-3.5">
          <span className="font-jetbrains text-[10.5px] text-dim">
            printed MAY 12 &middot; matrix {MATRIX_DIGEST}
          </span>
          <span className="font-jetbrains text-[10.5px] text-ink">
            today &middot; still {MATRIX_DIGEST}{' '}
            <span
              className="text-data-green transition-opacity duration-[400ms]"
              style={{ opacity: check ? 1 : 0.35 }}
            >
              &#10003; verified
            </span>{' '}
            &middot; reprints 0
          </span>
        </div>
      </div>

      {/* RIGHT — what scanning it resolves to. */}
      <div className="flex flex-col gap-5 bg-bg-warm px-7 pb-[22px] pt-6">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="font-jetbrains text-[10.5px] tracking-[0.15em] text-dim">
            WHAT SCANNING IT RESOLVES TO
          </span>
          <span
            className="rounded-full bg-brand-600 px-[9px] py-0.5 font-jetbrains text-[10px] tracking-[0.08em] text-white transition-transform duration-[450ms]"
            style={{ transform: hit ? 'scale(1.02)' : 'scale(1)' }}
          >
            {STEPS[step].date}
          </span>
        </div>
        <div className="flex flex-1 items-center justify-center py-1.5">
          <div
            aria-hidden="true"
            className="h-[576px] w-[265px] rounded-[34px] transition-[transform,box-shadow] duration-[450ms]"
            style={{
              transform: hit ? 'scale(1.02)' : 'scale(1)',
              boxShadow: hit
                ? '0 0 0 5px color-mix(in srgb,var(--color-brand-600) 30%,transparent)'
                : '0 0 0 0 transparent',
            }}
          >
            <PhoneFrame>
              <div className="relative h-full text-[#101028]">
                <MenuScreen visible={step === 0} />
                <WaitlistScreen visible={step === 1} />
                <RecapScreen visible={step === 2} />
              </div>
            </PhoneFrame>
          </div>
        </div>
        <div className="border-t border-line pt-3.5">
          <span className="font-jetbrains text-[10.5px] text-dim">
            302 &middot; go.acme.com/menu &rarr;{' '}
            <span className="text-ink">{STEPS[step].dest}</span> &middot; same printed code
          </span>
        </div>
      </div>
    </div>
  );
}
